/**
 * StageBase — what every living-events stage gets and must provide.
 *
 * A stage DRESSES a venue for one live event (props, lights, particles) and
 * animates it per frame; it never touches versonas — the server parks the
 * crowd, EnvironmentModule poses it. One stage per live event row; disposed
 * when the row ends or the player walks out of range.
 */
import * as THREE from 'three/webgpu';
import type { EventKindDef } from '@shared/living_events.js';
import { isShared } from '../fx_textures.js';
import { disposeLater } from '../../../lib/gpu_dispose.js';

export interface StageCtx {
  /** the parent group (world coordinates) */
  root: THREE.Group;
  groundAt(x: number, z: number): number;
  x: number;
  z: number;
  /** ground height at (x, z) when the stage was built */
  y: number;
  title: string;
  detail: string;
  def: EventKindDef;
  hostId: string;
  participantIds: string[];
  /** a versona's live body position (null outside the AOI window) */
  versonaPosition(id: string): { x: number; y: number; z: number } | null;
  /** real seconds the event has left */
  remainingS(): number;
}

export interface Stage {
  /** world-space anchor, for HUD/probes */
  readonly group: THREE.Group;
  update(t: number, dt: number): void;
  dispose(): void;
  /** what the stage is showing right now — probe surface */
  describe(): Record<string, number | string | boolean>;
}

const propMaterials = new Map<string, THREE.MeshStandardMaterial>();
/** One shared lit material per (colour, roughness) for every prop of every stage — never a pipeline per box. */
export function propMaterial(color: number, roughness = 0.9): THREE.MeshStandardMaterial {
  const key = `${color}|${roughness}`;
  let m = propMaterials.get(key);
  if (!m) { m = new THREE.MeshStandardMaterial({ color, roughness }); m.userData.shared = true; propMaterials.set(key, m); }
  return m;
}

/** Tear a stage down: geometries + UNIQUE materials/maps go to the deferred queue (the warp-teardown law); shared ones stay. */
export function disposeGroup(g: THREE.Group): void {
  g.parent?.remove(g);
  g.traverse((o) => {
    // A generated prop (ceremony_props_loader) is a CLONE sharing its master's
    // geometry and materials — the master outlives every stage; never free them.
    if (o.userData.sharedAsset === true) return;
    for (let p = o.parent; p && p !== g; p = p.parent) if (p.userData.sharedAsset === true) return;
    const m = o as THREE.Mesh;
    if (m.geometry && !(o as THREE.Sprite).isSprite) disposeLater(m.geometry);
    const mat = (o as THREE.Mesh | THREE.Sprite).material as THREE.Material | THREE.Material[] | undefined;
    if (Array.isArray(mat)) mat.forEach((x) => { if (!isShared(x)) disposeLater(x); });
    else if (mat && !isShared(mat)) {
      const map = (mat as THREE.SpriteMaterial).map;
      if (map && (map as THREE.CanvasTexture).isCanvasTexture && (mat as THREE.SpriteMaterial & { ownsMap?: boolean }).ownsMap) disposeLater(map);
      disposeLater(mat);
    }
  });
}

/** A cloth banner on a pole with text (festival banners, civic standards). */
export function makeBanner(text: string, color: number, height = 4.2): THREE.Group {
  const g = new THREE.Group();
  const pole = new THREE.Mesh(new THREE.CylinderGeometry(0.05, 0.06, height, 6), propMaterial(0x5b4632, 0.9));
  pole.position.y = height / 2;
  g.add(pole);
  const cv = document.createElement('canvas');
  cv.width = 256; cv.height = 384;
  const c = cv.getContext('2d')!;
  c.fillStyle = `#${color.toString(16).padStart(6, '0')}`;
  c.fillRect(0, 0, 256, 384);
  c.fillStyle = 'rgba(0,0,0,0.18)';
  c.fillRect(0, 0, 256, 28);
  c.save();
  c.translate(128, 200);
  c.rotate(-Math.PI / 2);
  c.textAlign = 'center'; c.textBaseline = 'middle';
  c.font = '700 34px system-ui';
  c.fillStyle = '#fff6e6';
  c.fillText(text.slice(0, 22), 0, 0);
  c.restore();
  const tex = new THREE.CanvasTexture(cv);
  tex.colorSpace = THREE.SRGBColorSpace;
  const mat = new THREE.MeshStandardMaterial({ map: tex, side: THREE.DoubleSide, roughness: 0.8 });
  (mat as THREE.MeshStandardMaterial & { ownsMap?: boolean }).ownsMap = true;
  const cloth = new THREE.Mesh(new THREE.PlaneGeometry(0.9, 1.35, 6, 8), mat);
  cloth.position.set(0.47, height - 0.8, 0);
  cloth.name = 'cloth';
  g.add(cloth);
  return g;
}

/** Gentle cloth wave — call per frame. */
export function waveCloth(g: THREE.Group, t: number, phase = 0): void {
  const cloth = g.getObjectByName('cloth') as THREE.Mesh | undefined;
  if (!cloth) return;
  cloth.rotation.y = Math.sin(t * 1.3 + phase) * 0.18;
  cloth.rotation.z = Math.sin(t * 2.1 + phase) * 0.05;
}
